import React, {  useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { AppContext } from "Context/AppContext";
import Label from "Components/Common/components/Label";
import { Link } from "react-router-dom";
import { CREATE, LIST_CREATE, LIST_DELETE, LIST_READ, LISTS, ListsHeaders, TAXONOMY } from "./constants";
import useTable from "Components/Common/Table/useTable";
import NoDataComponent from "Components/Common/NoDataComponent";
import Table from "Components/Common/Table/Table";
import { toast } from "react-toastify";
import CopyModal from "./CopyModal";
import CreateListDialog from "./CreateListDialog";
import { copyListItem, deleteListItem, getList, saveList } from "./Services/ListServices";

const ListsView = () => {
  const appContext = useContext(AppContext);
  const industryId = appContext.get_selected_industry() && appContext?.get_selected_industry()?.id;
  const { headers, data, sort, setData, handleSortByColumn } = useTable({ table_headers: ListsHeaders });
  const [recordPerPage, setRecordPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selectedListItem, setSelectedListItem] = useState<{ id: string, name: string } | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState<boolean>(false);
  const [createListInProgress, setCreateListInProgress] = useState<boolean>(false);

  const createPermission = appContext?.pm?.checkPermission(LIST_CREATE);
  const readPermission = appContext.pm.checkPermission(LIST_READ);
  const deletePermission = appContext.pm.checkPermission(LIST_DELETE);

  const openCreateListDialog = () => {
    if (!createPermission) { history.push("/workspace/module/permission/denied"); return; }
    setShowCreateDialog(true);
  }

  const closeCreateListDialog = () => setShowCreateDialog(false);

  const appData = useContext(AppContext);
  const history = useHistory();

  useEffect(() => {
    if (!readPermission) {
      history.push("/workspace/module/permission/denied");
      return;
    }
    if (industryId) fetchTableData();
  }, [appData.channelKey]);

  const fetchTableData = async () => {
    if (!industryId) return
    try {
      setLoading(true);
      const res = await getList(industryId);
      if (res?.data?.done) {
        setData(res?.data?.data);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleRecordPerPageChange = (perPage: number) => {
    setRecordPerPage(perPage);
    setPage(1);
  };

  const handleEdit = (index: number) => {
    if (page > 1) {
      index = ((page - 1) * recordPerPage) + index
    }
    const list: any = data[index];
    history.push(`lists/view/${list?.id}`);
  };

  const handleDelete = async (index: number) => {
    if (deletePermission) {
      if (page > 1) {
        index = ((page - 1) * recordPerPage) + index
      }

      const list: any = data[index];
      const deleteRes = await deleteListItem(list?.id);
      if (deleteRes) {
        toast.success("Deleted Succesfully");
        await fetchTableData();
      } else toast.warning("Delete Unsuccessful");
    }
    else toast.info("You don't have the permission to delete")
  };

  const createCopy = async () => {
    if (selectedListItem == null) return
    if (createPermission) {
      const res = await copyListItem(selectedListItem?.id, selectedListItem?.name);
      setSelectedListItem(null);
      if (res) {
        toast.success(res?.message ?? "Copied Succesfully");
        await fetchTableData();
      } else toast.error(res?.message ?? "Copy Unsuccessful");
    }
    else toast.info("You don't have the permission to create")
  };

  const handleCopy = (index: number) => {
    if (createPermission) {
      const paginatedIndex = ((page - 1) * recordPerPage) + index
      const list: any = data[paginatedIndex];
      setSelectedListItem({ id: list?.id, name: "Copy of " + list?.name })
    }
    else toast.info("You don't have the permission to create copy")
  }

  const handleCreate = () => {
    if (createPermission) {
      openCreateListDialog();
    } else toast.info("You dont have permission to create")
  };

  const handleSave = async (listName: string) => {
    try {
      if (createPermission) {
        if (listName && industryId) {
          setCreateListInProgress(true)
          const requestBody = {
            name: listName,
            industry_id: industryId
          };
          const save = await saveList(requestBody);
          if (save?.data?.done === true) {
            toast.success(save?.data?.message ?? "List created succesfully")
            closeCreateListDialog();
            if (save?.data?.data?.id) {
              history.push(`/gms/taxonomy/lists/edit/${save?.data?.data?.id}`);
            }
          } else {
            toast.error(save?.data?.message ?? "Failed to create list")
          }
        }
        else {
          toast.info("List name is mandatory")
        }
      }
      else toast.info("You don't have the permission to create list")
    } catch (error) {
      console.log(error);
      toast.error("Failed to create list")
    } finally {
      setCreateListInProgress(false)
    }
  };


  return (
    <>
      <div className="tw-mt-1">
        <nav className="tw-flex" aria-label="Breadcrumb">
          <ol className="tw-inline-flex tw-items-center tw-space-x-1 tw-md:space-x-2 tw-rtl:space-x-reverse">
            <li className="tw-inline-flex tw-items-center tw-pointer-events-none">
              <Link to="#">
                <Label
                  message={TAXONOMY}
                  fontSize={"0.75"}
                  fontWeight="400"
                  color="#5c5f62"
                />
              </Link>
            </li>
            <li>/</li>
            <li className="tw-inline-flex tw-items-center tw-pointer-events-none" aria-current="page">
              <Label
                message={LISTS}
                fontSize={"0.75"}
                fontWeight="500"
                color="#5c5f62"
              />
            </li>
          </ol>
        </nav>
      </div>
      <div className={`tw-px-[21px] tw-flex tw-justify-between tw-mt-2`}>
        <div>
          <label
            className={`tw-text-black tw-font-noto-sans tw-text-xl tw-font-semibold tw-leading-[25.2px]`}
          >
            {LISTS}
          </label>
          <div className="tw-w-[33px] tw-h-[26px] tw-ml-4 tw-px-3 tw-py-0.5 tw-bg-zinc-200 tw-rounded-[44px] tw-border tw-justify-center tw-items-center tw-gap-1 tw-inline-flex">
            {data?.length}
          </div>
        </div>
        <button
          onClick={handleCreate}
          className="tw-bg-blue-800 tw-text-white tw-hover:bg-blue-400 tw-font-bold tw-py-2 tw-px-4 tw-rounded tw-items-center"
        >
          {CREATE}
        </button>
      </div>
      <div
        className={`tw-ml-5 tw-mt-2 tw-box-border tw-w-auto tw-border-t-[1px] tw-border-solid tw-border-lightgray-200`}
      />
      <Table
        headers={headers}
        recordPerPage={recordPerPage}
        setRecordPerPage={handleRecordPerPageChange}
        handlePageChange={setPage}
        data={data}
        loading={loading}
        handleSortByColumn={handleSortByColumn}
        sort={sort}
        page={page}
        handleViewLog={handleEdit}
        handleDelete={handleDelete}
        handleCopy={handleCopy}
        NoDataComponent={() => (
          <>
            <NoDataComponent />
            <div className="tw-container tw-py-4 tw-px-10 tw-min-w-full tw-grid tw-place-items-center">
              <button
                onClick={handleCreate}
                className="tw-bg-blue-800 tw-text-white tw-hover:bg-blue-400 tw-font-bold tw-py-2 tw-px-4 tw-rounded tw-items-center"
              >
                {CREATE}
              </button>
            </div>
          </>
        )}
        handleColumnDrag={() => { }}
      />
      {selectedListItem != null && <CopyModal showModal={selectedListItem != null} closeModal={() => { setSelectedListItem(null) }} onConfirm={createCopy} listName={selectedListItem?.name}
        setCopyListName={(name: string) =>
          setSelectedListItem({
            id: selectedListItem.id,
            name: name
          })
        }
      />}
      {showCreateDialog && <CreateListDialog dialogHeader="Create List" labelName="List Name" onClose={closeCreateListDialog} onSubmit={handleSave} message="Creating your list ..." actionLabel="Create" key="create-list-dialog" showIndicator={createListInProgress} />
      }
    </>
  );
};

export default ListsView;